import type { Nullable } from '@/types/utility';

/**
 * Helpers for working with peak estimations and calibration results.
 *
 * The system has two sources for compound estimation:
 * 1. Calibration results - from calibration_result table for samples in sequences with attached calibrations
 * 2. Manual estimations - from siebox_compoundestimation table for manual peak labeling
 *
 * Calibration results take priority over manual estimations when both are present.
 */

type Compound = {
  id: number;
  name: Nullable<string>;
};

type Estimation = {
  id: number;
  amount: Nullable<number>;
  unit: Nullable<string>;
  compound: Nullable<Compound>;
  peak_id: number;
};

type CalibrationResult = {
  id: number;
  calc_concentration: Nullable<number>;
  unit: Nullable<string>;
  compound: Compound;
};

type EstimationWithSource = Estimation & {
  from_calibration?: boolean;
};

type Peak = {
  id: number;
  calibration_results?: Nullable<CalibrationResult[]>;
  estimations?: Nullable<Estimation[]>;
};

type PeakWithEstimation = Peak & {
  estimation: Nullable<EstimationWithSource>;
};

type PeakWithEstimationsArray = Peak & {
  estimations: Estimation[];
};

/**
 * Determines which compound estimation to use for a peak.
 *
 * Priority:
 * 1. calibration_results[0] - results from calibration system (for quantitated samples)
 * 2. estimations[0] - manual peak labeling from siebox_compoundestimation
 * 3. null/undefined - no estimation available
 *
 * @param peak - Peak object with calibration_results and estimations arrays
 * @returns Normalized estimation object or null if none available
 */
export function getPeakEstimation(peak: Peak): Nullable<EstimationWithSource> {
  const calibrationResult = peak.calibration_results?.[0];
  const estimation = peak.estimations?.[0];

  if (calibrationResult) {
    return {
      id: calibrationResult.id,
      amount: calibrationResult.calc_concentration,
      unit: calibrationResult.unit,
      compound: calibrationResult.compound,
      peak_id: peak.id,
      from_calibration: true,
    };
  }

  return estimation || null;
}

/**
 * Maps peak objects to include normalized estimation.
 *
 * @param peaks - Array of peak objects
 * @returns Peaks with normalized estimation field
 */
export function mapPeaksWithEstimation(peaks: Peak[] = []): PeakWithEstimation[] {
  return peaks.map((peak) => ({
    ...peak,
    estimation: getPeakEstimation(peak),
  }));
}

/**
 * Maps calibration results to estimations array.
 * Used by components that work with estimations array format.
 *
 * If manual estimations already exist, they are preserved.
 * Otherwise, calibration_results are added to estimations array.
 *
 * @param peaks - Array of peak objects
 * @returns Peaks with estimations array containing either manual estimations or calibration results
 */
export function mapCalibrationResultsToEstimations(peaks: Peak[] = []): PeakWithEstimationsArray[] {
  return peaks.map((peak) => {
    if (peak.estimations && peak.estimations.length > 0) {
      return { ...peak, estimations: peak.estimations };
    }

    if (peak.calibration_results && peak.calibration_results.length > 0) {
      const calibrationResult = peak.calibration_results[0];
      return {
        ...peak,
        estimations: [
          {
            id: calibrationResult.id,
            amount: calibrationResult.calc_concentration,
            unit: calibrationResult.unit,
            compound: calibrationResult.compound,
            peak_id: peak.id,
          },
        ],
      };
    }

    return { ...peak, estimations: [] };
  });
}
